// frontend/src/modules/integrations/pushFeedback.ts
//
// Shared wording for the four "Push to QuickBooks" surfaces (invoice, expense,
// payroll run, payment) so they can't drift apart.

import { toast } from 'sonner';
import type { QuickBooksPushWarning } from './types';

type ConfirmFn = (opts: {
  title: string;
  description: string;
  confirmLabel?: string;
  destructive?: boolean;
}) => Promise<boolean>;

/**
 * QuickBooks has no idempotency key, so a forced re-push genuinely creates a
 * SECOND entry in the customer's books — say so plainly rather than framing it
 * as a harmless retry.
 */
export function confirmRePush(confirm: ConfirmFn, subject: string): Promise<boolean> {
  return confirm({
    title: `Push this ${subject} to QuickBooks again?`,
    description:
      `This ${subject} is already in QuickBooks. Pushing again creates a second entry there — `
      + 'it does not update the existing one. Only do this if the original was deleted in QuickBooks.',
    confirmLabel: 'Push again',
    destructive: true,
  });
}

interface PushResultLike {
  already_pushed?: boolean;
  warning?: QuickBooksPushWarning;
}

/** Toast the outcome of a push: deduped, warned, or clean. */
export function reportPushResult(res: PushResultLike | null | undefined): void {
  if (res?.already_pushed) {
    toast.success('Already in QuickBooks', {
      description: 'This was pushed before, so nothing new was created.',
    });
    return;
  }
  if (res?.warning) {
    toast.warning('Pushed to QuickBooks — check the totals', { description: res.warning.message });
    return;
  }
  toast.success('Pushed to QuickBooks');
}
